import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { throttle } from 'lodash';
import { withInvariant } from '../../utils/withInvariant';
import { Box, BoxProps } from './Box';

interface VirtualPageListProps {
    renderItem: (index: number) => React.ReactNode;
    itemsLength: number;
    itemHeight: number;
    styles?: BoxProps;
    yItemStock?: number;
}

const CHECK_DELAY = 17;

export const VirtualPageList = withInvariant(
    (props: VirtualPageListProps) => {
        const { renderItem, itemsLength, itemHeight, styles, yItemStock = 6 } = props;
        const [, setScrollTop] = useState(0);
        const mainContainerRef = useRef<HTMLDivElement>(null);
        const wasTopOffsetAddedRef = useRef(false);

        const startIndexRef = useRef(0);
        const endIndexRef = useRef(document.documentElement.clientHeight / itemHeight);

        const topOffsetRef = useRef(0);

        const renderItems = [];

        useEffect(() => {
            if (!mainContainerRef.current) {
                return;
            }

            mainContainerRef.current.style.height = `${itemsLength * itemHeight}px`;

            if (!wasTopOffsetAddedRef.current) {
                wasTopOffsetAddedRef.current = true;

                topOffsetRef.current =
                    // Ищем разницу между верхней границей контейнера и верхней границей документа,
                    // чтобы невелировать topScroll при возвращении на страницу с сохраненной позицией скролла
                    mainContainerRef.current.getClientRects()[0].top -
                    document.documentElement.getClientRects()[0].top;
            }
        }, [itemsLength, itemHeight]);

        useEffect(
            () => () => {
                document.body.removeAttribute('style');
            },
            [],
        );

        useLayoutEffect(() => {
            if (!itemHeight) {
                return;
            }

            const onScroll = throttle(() => {
                if (document.documentElement.scrollTop < topOffsetRef.current) {
                    return;
                }

                startIndexRef.current = Math.floor(
                    (document.documentElement.scrollTop - topOffsetRef.current) / itemHeight,
                );

                endIndexRef.current = Math.floor(
                    (document.documentElement.scrollTop + document.documentElement.clientHeight) /
                        itemHeight,
                );

                setScrollTop(document.documentElement.scrollTop!);
            }, CHECK_DELAY);

            document.addEventListener('scroll', onScroll);
            onScroll();

            return () => {
                document.removeEventListener('scroll', onScroll);
            };
        }, [itemHeight]);

        for (
            let i = startIndexRef.current - yItemStock;
            i <= endIndexRef.current + yItemStock;
            i++
        ) {
            if (i < 0 || i >= itemsLength) {
                continue;
            }

            renderItems.push(
                <Box
                    key={i}
                    position="absolute"
                    width="100%"
                    height={itemHeight}
                    top={`${i * itemHeight}px`}
                >
                    {renderItem(i)}
                </Box>,
            );
        }

        return (
            <Box position="relative" ref={mainContainerRef} width="100%" {...styles}>
                {renderItems}
            </Box>
        );
    },
    () => null,
);
